import React, { useCallback, useMemo } from 'react';
import { RADAR_DIMENSION_KEYS, RadarDimensionKey, getRadarDimensionLabel, getRadarDimensionTooltipMeta } from '../../utils/radarDimensions';

const RADAR_DIMENSION_KEY_SET = new Set<string>(RADAR_DIMENSION_KEYS);

/* ── Constants ── */

/* ── Types ── */
export interface HeatmapCandidate {
  name: string;
  resumeFilename: string;
  score: number;
  dimensionScores: Record<string, number>;
  dimensionExplanations?: Record<string, string>;
}

interface DimensionHeatmapProps {
  dimensionWeights: Record<string, number>;
  candidates: HeatmapCandidate[];
  displayResumeName: (f: string) => string;
  maxCandidates?: number;
  truncateCandidateName?: boolean;
  showCopyAction?: boolean;
}

/* ── Color scale: red → yellow → green ── */
function scoreColor(score: number): string {
  if (score >= 80) return 'rgba(16, 185, 129, 0.75)';   // green
  if (score >= 65) return 'rgba(52, 211, 153, 0.55)';   // light green
  if (score >= 50) return 'rgba(251, 191, 36, 0.55)';   // amber
  if (score >= 35) return 'rgba(251, 146, 60, 0.55)';   // orange
  return 'rgba(239, 68, 68, 0.55)';                     // red
}

function scoreTextColor(score: number): string {
  if (score >= 80) return '#065f46';
  if (score >= 65) return '#065f46';
  if (score >= 50) return '#78350f';
  if (score >= 35) return '#7c2d12';
  return '#7f1d1d';
}

/* ── Component ── */
const DimensionHeatmap: React.FC<DimensionHeatmapProps> = ({
  dimensionWeights,
  candidates,
  displayResumeName,
  maxCandidates = 25,
  truncateCandidateName = true,
  showCopyAction = true,
}) => {
  const shown = useMemo(() => candidates.slice(0, maxCandidates), [candidates, maxCandidates]);

  const dimensions = useMemo(
    () => RADAR_DIMENSION_KEYS.map(key => ({
      key,
      label: getRadarDimensionLabel(key, 'full'),
      weight: Math.max(0, Number(dimensionWeights[key] || 0)),
    })),
    [dimensionWeights],
  );

  const handleCopy = useCallback(async () => {
    const header = ['Candidate', ...dimensions.map(d => d.label), 'Overall Score'].join('\t');
    const weightRow = ['Job Weight', ...dimensions.map(d => `${Math.round(d.weight * 100)}%`), ''].join('\t');
    const rows = shown.map(c => {
      const candidateName = displayResumeName(c.resumeFilename);
      const scores = dimensions.map(d => `${Math.round(c.dimensionScores[d.key] ?? 0)}%`);
      return [candidateName, ...scores, `${Math.round(c.score)}%`].join('\t');
    });
    await navigator.clipboard.writeText([header, weightRow, ...rows].join('\n'));
  }, [shown, dimensions, displayResumeName]);

  const getCellTitle = useCallback((candidate: HeatmapCandidate, dimensionKey: string, score: number) => {
    const label = RADAR_DIMENSION_KEY_SET.has(dimensionKey)
      ? getRadarDimensionLabel(dimensionKey as RadarDimensionKey, 'full')
      : dimensionKey;
    const meta = RADAR_DIMENSION_KEY_SET.has(dimensionKey)
      ? getRadarDimensionTooltipMeta(dimensionKey as RadarDimensionKey)
      : undefined;
    const explanation = candidate.dimensionExplanations?.[dimensionKey]?.trim();
    const lines = [`${label}: ${Math.round(score)}%`];
    if (meta) {
      lines.push(meta.summary);
      lines.push(`Standards: ${meta.onetMapping} / ${meta.shrmMapping}`);
    }
    if (explanation) lines.push(explanation);
    return lines.join('\n');
  }, []);

  if (!dimensionWeights || Object.keys(dimensionWeights).length === 0 || shown.length === 0) return null;

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-base)' }}>Skill Match Heatmap</h2>
        {showCopyAction && (
          <button
            className="v2-btn v2-btn-ghost"
            onClick={handleCopy}
            title="Copy heatmap data to clipboard"
            style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            Copy Data
          </button>
        )}
      </div>

      <div style={{
        overflow: 'auto',
        maxHeight: 520,
        borderRadius: 8,
        border: '1px solid var(--border-light, #e5e7eb)',
        position: 'relative',
        isolation: 'isolate',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)', minWidth: 860 }}>
          <thead>
            <tr>
              <th style={{
                padding: '8px 12px', textAlign: 'left',
                borderBottom: '2px solid var(--border-light, #e5e7eb)',
                backgroundColor: 'var(--bg-secondary, #f9fafb)',
                position: 'sticky',
                left: 0,
                top: 0,
                zIndex: 4,
                minWidth: 220,
              }}>
                Candidate
              </th>
              {dimensions.map((d) => (
                <th key={d.key} style={{
                  padding: '8px 12px', textAlign: 'left',
                  borderBottom: '2px solid var(--border-light, #e5e7eb)',
                  backgroundColor: 'var(--bg-secondary, #f9fafb)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  minWidth: 72,
                }}>
                  {d.label}
                </th>
              ))}
              <th style={{
                padding: '8px 12px', textAlign: 'left',
                borderBottom: '2px solid var(--border-light, #e5e7eb)',
                backgroundColor: 'var(--bg-secondary, #f9fafb)',
                position: 'sticky',
                top: 0,
                zIndex: 3,
                minWidth: 88,
              }}>
                Overall Score
              </th>
            </tr>
            <tr>
              <th style={{
                padding: '6px 12px',
                textAlign: 'left',
                borderBottom: '1px solid var(--border-light, #e5e7eb)',
                backgroundColor: 'var(--bg-secondary, #f9fafb)',
                color: 'var(--text-muted)',
                position: 'sticky',
                left: 0,
                top: 35,
                zIndex: 4,
                fontWeight: 600,
              }}>
                Job Weight
              </th>
              {dimensions.map((d) => (
                <th key={`${d.key}-weight`} style={{
                  padding: '6px 12px',
                  textAlign: 'left',
                  borderBottom: '1px solid var(--border-light, #e5e7eb)',
                  backgroundColor: 'var(--bg-secondary, #f9fafb)',
                  color: 'rgba(239, 68, 68, 0.9)',
                  position: 'sticky',
                  top: 35,
                  zIndex: 3,
                  fontWeight: 700,
                  fontSize: 11,
                }}>
                  {Math.round(d.weight * 100)}%
                </th>
              ))}
              <th style={{
                padding: '6px 12px',
                borderBottom: '1px solid var(--border-light, #e5e7eb)',
                backgroundColor: 'var(--bg-secondary, #f9fafb)',
                position: 'sticky',
                top: 35,
                zIndex: 3,
              }} />
            </tr>
          </thead>
          <tbody>
            {shown.map((c, rowIndex) => {
              const candidateName = displayResumeName(c.resumeFilename);
              const rowBg = rowIndex % 2 === 0 ? 'var(--bg-primary, #ffffff)' : 'var(--bg-secondary, #f9fafb)';
              return (
                <tr key={`${c.resumeFilename}-${rowIndex}`} style={{ borderBottom: '1px solid var(--border-light, #e5e7eb)' }}>
                  <td style={{
                    padding: '8px 12px',
                    fontWeight: 500,
                    backgroundColor: rowBg,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    color: 'var(--text-secondary)',
                  }}>
                    <div
                      title={candidateName}
                      style={truncateCandidateName
                        ? {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 210,
                        }
                        : {
                          whiteSpace: 'nowrap',
                        }}
                    >
                      {candidateName}
                    </div>
                  </td>
                  {dimensions.map(d => {
                    const score = c.dimensionScores[d.key] ?? 0;
                    return (
                      <td key={`${c.resumeFilename}-${d.key}`} style={{
                        padding: '4px 2px', textAlign: 'center',
                        backgroundColor: rowBg,
                      }}>
                        <div
                          title={getCellTitle(c, d.key, score)}
                          style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 42, height: 28, borderRadius: 6,
                          backgroundColor: scoreColor(score),
                          color: scoreTextColor(score),
                          fontWeight: 700, fontSize: 12,
                        }}>
                          {Math.round(score)}%
                        </div>
                      </td>
                    );
                  })}
                  <td style={{ padding: '6px 2px', textAlign: 'center', backgroundColor: rowBg }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 52, height: 28, borderRadius: 999,
                      padding: '0 8px',
                      backgroundColor: scoreColor(c.score),
                      color: scoreTextColor(c.score),
                      fontWeight: 800,
                      fontSize: 12,
                    }}>
                      {Math.round(c.score)}%
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: 'rgba(239, 68, 68, 0.55)' }} /> 0–34
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: 'rgba(251, 146, 60, 0.55)' }} /> 35–49
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: 'rgba(251, 191, 36, 0.55)' }} /> 50–64
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: 'rgba(52, 211, 153, 0.55)' }} /> 65–79
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: 'rgba(16, 185, 129, 0.75)' }} /> 80+
        </span>
      </div>
    </div>
  );
};

export default DimensionHeatmap;
